import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BlogWYSIWYG from '../components/blog/BlogWYSIWYG';
import { blogService, BlogCategory } from '../services/blogService';
import { useUser } from '../contexts/UserContext';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: white;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: white;
  font-weight: 500;
  font-size: 0.95rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 4px;
  background-color: rgb(31, 31, 31);
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #666;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 4px;
  background-color: rgb(31, 31, 31);
  color: white;
  font-size: 1rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  option {
    background-color: rgb(31, 31, 31);
    color: white;
  }
`;

const FeaturedImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FeaturedImageOptions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ImageOption = styled.div`
  flex: 1;
  min-width: 200px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: white;
  cursor: pointer;
  user-select: none;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  ${props => {
    switch (props.$variant) {
      case 'danger':
        return `
          background-color: #dc3545;
          color: white;
          &:hover {
            background-color: #c82333;
          }
        `;
      case 'secondary':
        return `
          background-color: #6c757d;
          color: white;
          &:hover {
            background-color: #5a6268;
          }
        `;
      default:
        return `
          background-color: #007bff;
          color: white;
          &:hover {
            background-color: #0056b3;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.1);
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #ff6b6b;
`;

const SuccessMessage = styled.div`
  color: #51cf66;
  background-color: rgba(81, 207, 102, 0.1);
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #51cf66;
`;

const HelpText = styled.p`
  color: #999;
  font-size: 0.85rem;
  margin: 0;
`;

const PreviewContent = styled.article`
  color: #ddd;
  font-size: 1.1rem;
  line-height: 1.8;
  background-color: rgb(31, 31, 31);
  padding: 2rem;
  border-radius: 8px;
  margin-top: 2rem;

  h1 {
    color: white;
    font-size: 2rem;
    margin: 2rem 0 1rem 0;
    line-height: 1.3;
  }

  h2 {
    color: white;
    font-size: 1.75rem;
    margin: 1.75rem 0 1rem 0;
    line-height: 1.3;
  }

  h3 {
    color: white;
    font-size: 1.5rem;
    margin: 1.5rem 0 1rem 0;
    line-height: 1.3;
  }

  p {
    margin: 1rem 0;
  }

  a {
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
  }

  li {
    margin: 0.5rem 0;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1.5rem 0;
  }
`;

const NewCategoryInput = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const BlogEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const isEditing = Boolean(id);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [extractedImages, setExtractedImages] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  // Note: Database stores as "admin" but TypeScript interface uses is_admin
  const isAdmin = (user?.user_permissions as any)?.admin || user?.user_permissions?.is_admin;

  useEffect(() => {
    fetchCategories();
    if (isEditing && id) {
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const fetchPost = async () => {
    if (!id) return;

    setInitialLoading(true);
    try {
      const post = await blogService.getPost(id);
      setTitle(post.title);
      setContent(post.content);
      setCategoryId(post.category_id || '');
      setFeaturedImage(post.featured_image || '');
      setIsAnonymous(post.is_anonymous);
      setIsHidden(post.is_hidden);
    } catch (err: any) {
      setError(err.message || 'Failed to load blog post');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    // Extract images from content whenever content changes
    const images = blogService.extractImagesFromContent(content);
    setExtractedImages(images);
  }, [content]);

  const fetchCategories = async () => {
    try {
      const data = await blogService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const newCategory = await blogService.createCategory(newCategoryName);
      setCategories([...categories, newCategory]);
      setCategoryId(newCategory.id);
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (err: any) {
      setError(err.message || 'Failed to create category');
    }
  };

  const handleSubmit = async (e: React.FormEvent, publish: boolean = false) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!content.trim()) {
      setError('Content is required');
      return;
    }

    setLoading(true);

    try {
      const postData = {
        title: title.trim(),
        content: content.trim(),
        category_id: categoryId || undefined,
        featured_image: featuredImage || undefined,
        is_anonymous: isAnonymous,
        is_published: publish,
        is_hidden: isHidden,
      };

      if (isEditing && id) {
        await blogService.updatePost(id, postData);
        setSuccess(publish ? 'Blog post published successfully!' : 'Draft saved successfully!');
        
        // Only navigate away if publishing
        if (publish) {
          setTimeout(async () => {
            const post = await blogService.getPost(id);
            navigate(`/blog/${post.id}/${post.slug}`);
          }, 1500);
        }
      } else {
        const newPost = await blogService.createPost(postData);
        setSuccess(publish ? 'Blog post published successfully!' : 'Draft saved successfully!');
        
        // Navigate to edit mode for new posts or to published post if published
        if (publish) {
          setTimeout(() => {
            navigate(`/blog/${newPost.id}/${newPost.slug}`);
          }, 1500);
        } else {
          // Update URL to edit mode without navigation
          setTimeout(() => {
            navigate(`/blog/edit/${newPost.id}`, { replace: true });
          }, 1500);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !isAdmin) return;

    if (!window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await blogService.deletePost(id);
      setSuccess('Blog post deleted successfully!');
      
      setTimeout(() => {
        navigate('/blog');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to delete blog post');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Container>
        <Title>Loading...</Title>
      </Container>
    );
  }

  if (showPreview) {
    return (
      <Container>
        <Title>Preview</Title>
        <ButtonGroup>
          <Button type="button" $variant="secondary" onClick={() => setShowPreview(false)}>
            Back to Editor
          </Button>
        </ButtonGroup>
        <PreviewContent dangerouslySetInnerHTML={{ __html: content }} />
      </Container>
    );
  }

  return (
    <Container>
      <Title>{isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}</Title>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <Form onSubmit={(e) => handleSubmit(e, false)}>
        <FormGroup>
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter blog post title"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="category">Category</Label>
          <Select
            id="category"
            value={categoryId}
            onChange={(e) => {
              if (e.target.value === 'new') {
                setShowNewCategory(true);
                setCategoryId('');
              } else {
                setCategoryId(e.target.value);
              }
            }}
          >
            <option value="">No Category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
            <option value="new">+ Add New Category</option>
          </Select>

          {showNewCategory && (
            <NewCategoryInput>
              <Input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="New category name"
              />
              <Button type="button" onClick={handleCreateCategory}>
                Add
              </Button>
              <Button
                type="button"
                $variant="secondary"
                onClick={() => {
                  setShowNewCategory(false);
                  setNewCategoryName('');
                }}
              >
                Cancel
              </Button>
            </NewCategoryInput>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Content *</Label>
          <BlogWYSIWYG content={content} onChange={setContent} />
          <HelpText>
            Use the toolbar to format your content. You can add headings, lists, links, and images.
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Featured Image</Label>
          <FeaturedImageSection>
            <FeaturedImageOptions>
              <ImageOption>
                <Label htmlFor="featuredImageUrl">Image URL</Label>
                <Input
                  id="featuredImageUrl"
                  type="url"
                  value={featuredImage}
                  onChange={(e) => setFeaturedImage(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
              </ImageOption>

              {extractedImages.length > 0 && (
                <ImageOption>
                  <Label htmlFor="selectFromContent">Or select from article</Label>
                  <Select
                    id="selectFromContent"
                    value={featuredImage}
                    onChange={(e) => setFeaturedImage(e.target.value)}
                  >
                    <option value="">Select an image</option>
                    {extractedImages.map((img, index) => (
                      <option key={index} value={img}>
                        Image {index + 1}
                      </option>
                    ))}
                  </Select>
                </ImageOption>
              )}
            </FeaturedImageOptions>
            <HelpText>
              Upload an image or select one from the images in your article content.
            </HelpText>
          </FeaturedImageSection>
        </FormGroup>

        <FormGroup>
          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="isAnonymous"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
            />
            <CheckboxLabel htmlFor="isAnonymous">
              Post anonymously (hide author information)
            </CheckboxLabel>
          </CheckboxGroup>
        </FormGroup>

        {isAdmin && (
          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="isHidden"
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
              />
              <CheckboxLabel htmlFor="isHidden">
                Hide post (Admin only - post won't appear in listings)
              </CheckboxLabel>
            </CheckboxGroup>
          </FormGroup>
        )}

        <ButtonGroup>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            disabled={loading}
          >
            {loading ? 'Publishing...' : 'Publish'}
          </Button>
          <Button
            type="button"
            $variant="secondary"
            onClick={() => setShowPreview(true)}
            disabled={loading || !content.trim()}
          >
            Preview
          </Button>
          <Button
            type="button"
            $variant="secondary"
            onClick={() => navigate('/blog')}
            disabled={loading}
          >
            Cancel
          </Button>
          {isEditing && isAdmin && (
            <Button
              type="button"
              $variant="danger"
              onClick={handleDelete}
              disabled={loading}
            >
              Delete
            </Button>
          )}
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default BlogEditor;
